/**
 * element-target-signature.js — relative identity for a descendant inside a
 * repeat-root unit (Polish 16C2).
 *
 * 16C1 established that the selectable UNIT is the repeat root. Restyling is a
 * different question, and the plan is explicit that the first draft conflated
 * three distinct ids: the clicked descendant eid, the repeat-root eid, and the
 * override TARGET eid. "Recolour this row of icons" means styling a
 * *descendant* of each unit — the SVG icon inside each `.cs-item` — so the lead's
 * target must map to the equivalent target inside every other unit.
 *
 * The identity is a pair:
 *   unitRootId              — which unit the target lives in (16C1's unit eid)
 *   relativeTargetSignature — where inside the unit, expressed structurally
 *
 * The signature is a STRUCTURAL PATH, not an index: each step carries the tag
 * and the authored class set, and the ordinal is only a tiebreaker among
 * siblings that are already identical in tag and class. That ordering is the
 * repo's standing rule ("Identify DOM elements by index alone" is forbidden —
 * use composite signatures) and it is what makes the signature survive a unit
 * whose children are in a different order or count.
 *
 * Resolution reports the TIER it needed:
 *   'exact'   — every step matched on tag + class set
 *   'relaxed' — at least one step fell back to tag-only (class sets diverge
 *               between units, e.g. an active/current variant on one card)
 *   null      — unresolvable; the caller must SKIP that unit rather than guess
 *
 * The relaxed tier exists because real corpora carry per-unit modifier classes;
 * refusing those outright would make restyling fail on exactly the decorated
 * card the user is looking at. What it must never do is resolve to the WRONG
 * element, which is what `scripts/tests/element-signature-corpus-check.js`
 * measures against positional ground truth across the whole adapter corpus.
 */

/** Builder-injected classes are runtime state, never authored identity. */
function authoredClasses(el) {
  const raw = el.getAttribute ? el.getAttribute('class') : null;
  if (!raw) return [];
  return String(raw)
    .split(/\s+/)
    .filter((c) => c && !c.startsWith('pb-'))
    .sort();
}

function sameClasses(a, b) {
  return a.length === b.length && a.every((c, i) => c === b[i]);
}

function tagOf(el) {
  return String(el.tagName || '').toLowerCase();
}

/**
 * Siblings of `el` that are indistinguishable from it at the given strictness —
 * the set the ordinal disambiguates within.
 */
function peers(parent, tag, classes, strict) {
  return Array.from(parent.children).filter((child) => {
    if (tagOf(child) !== tag) return false;
    return strict ? sameClasses(authoredClasses(child), classes) : true;
  });
}

/**
 * Describe where `target` sits inside `unitRoot`.
 *
 * @param {Element} unitRoot
 * @param {Element} target — unitRoot itself, or a descendant of it
 * @returns {{version: 1, steps: Array<{tag: string, classes: string[],
 *   ordinal: number, of: number}>, tag: string, classes: string[]}|null}
 *   null when `target` is not inside `unitRoot`.
 */
export function computeRelativeTargetSignature(unitRoot, target) {
  if (!unitRoot || !target) return null;
  const steps = [];
  let node = target;
  while (node && node !== unitRoot) {
    const parent = node.parentElement;
    if (!parent) return null; // walked out of the unit — not a descendant
    const tag = tagOf(node);
    const classes = authoredClasses(node);
    const sameShape = peers(parent, tag, classes, true);
    steps.push({ tag, classes, ordinal: sameShape.indexOf(node), of: sameShape.length });
    node = parent;
  }
  if (node !== unitRoot) return null;
  steps.reverse();
  return {
    version: 1,
    steps,
    tag: tagOf(target),
    classes: authoredClasses(target)
  };
}

/**
 * Find the element inside `unitRoot` that `signature` describes.
 *
 * @param {Element} unitRoot
 * @param {object} signature — from computeRelativeTargetSignature
 * @returns {{el: Element, tier: 'exact'|'relaxed'}|null}
 */
export function resolveRelativeTarget(unitRoot, signature) {
  if (!unitRoot || !signature || !Array.isArray(signature.steps)) return null;
  // An empty path means the target IS the unit root — "restyle the card
  // itself", which is a legitimate and unambiguous request.
  if (signature.steps.length === 0) return { el: unitRoot, tier: 'exact' };

  let node = unitRoot;
  let tier = 'exact';
  for (const step of signature.steps) {
    const strictMatches = peers(node, step.tag, step.classes, true);
    let next = strictMatches[step.ordinal] ?? null;
    if (!next) {
      // Class sets diverge between units (a modifier on one card). Fall back to
      // tag-only, which still refuses to cross a tag boundary.
      const loose = peers(node, step.tag, step.classes, false);
      next = loose[step.ordinal] ?? null;
      if (!next) return null;
      tier = 'relaxed';
    }
    node = next;
  }
  return { el: node, tier };
}

/**
 * Stable string form, for keying a merged override map without carrying the
 * object around. Not parsed back — identity only.
 */
export function serializeRelativeTargetSignature(signature) {
  if (!signature || !Array.isArray(signature.steps)) return '';
  return signature.steps
    .map((s) => `${s.tag}${s.classes.length ? '.' + s.classes.join('.') : ''}#${s.ordinal}`)
    .join('>');
}
